//
// Helper methods for marshaling API data
//

import { format } from "date-fns";
import { initOtherStudyContact, initUserPerms } from "./initialStates";
import { convertOncoreLastSyncTimeToDisplayFormat } from "app/shared/utils";

/**
 * Helper method to convert API-shaped data to a shape usable by the
 * UI Services & presentational components.
 *
 * @param {object} data Study Recruitment data as recieved from the API call
 * @returns {object} Marshaled Study Recruitment data
 */
export const marshalStudyRecruitmentDataToFormik = (data) => {
  if (!data) return {};

  const studyContacts = transformStudyContactsRole(data);

  const mainContact = studyContacts.find(
    (contact) => contact?.mainPointOfContact === true,
  );

  const publishedVersion =
    data?.studyWebsiteContentRevisions?.find(
      (version) => version.contentStatus === "PUBLISHED",
    ) ||
    data?.studyWebsiteContentRevisions?.find(
      (version) => version.contentStatus === "ORIGINAL",
    );
  const publishedVersionId = publishedVersion?.id;
  const publishedVersionContent = publishedVersion?.studyWebsiteContentDto;

  return {
    protocolId: data.protocolId,
    protocolNumber: data.protocolNumber,
    irbNumber: data.irbNumber,
    nctId: data.nctId,
    studyId: data.id,
    versions: formatVersions(data.studyWebsiteContentRevisions),
    lastOncoreSync: convertOncoreLastSyncTimeToDisplayFormat(
      data.oncoreLastSyncTime,
    ),
    studyListingStatus: data.studyListingStatus,
    outreachEnabled: data.meetsSelfServiceRecruitmentCriteria,
    studyContacts,
    studyContactId: mainContact?.id || "",
    name: formatMainContactName(mainContact),
    nameVisibility: mainContact?.nameVisibility ?? false,
    email: mainContact?.email || "",
    emailVisibility: mainContact?.emailVisibility ?? false,
    phone: mainContact?.phone || "",
    phoneVisibility: !!mainContact?.phone && !!mainContact?.phoneVisibility,
    versionId: publishedVersionId || "",
    officialStudyTitle: publishedVersionContent.title || "",
    studyTitle: publishedVersionContent.shortTitle || "",
    purpose: publishedVersionContent.briefSummary || "",
    inclusionCriteria: publishedVersionContent.inclusionCriteria || "",
    exclusionCriteria: publishedVersionContent.exclusionCriteria || "",
    prescreeningQuestionnaireUrl: data.studySurvey || "",
    prescreeningQuestionnaireEstimatedTime:
      data.surveyCompletionTimeEstimate || "",
  };
};

/**
 * Helper method to convert Formik-shaped data to a shape usable by the
 * Study Recruitment API
 *
 * @param {object} formData Study Recruitment data as recieved from the API call
 * @returns {object} Marshaled Study Recruitment data
 */
export const marshalFormikToStudyRecruitmentApi = (formData) => {
  const updatedStudyContacts = formData.studyContacts
    .filter((contact) => contact.firstName !== "Other")
    .map((contact) => {
      const isMainPointOfContact = contact.id === formData.studyContactId;

      if (isMainPointOfContact) {
        return {
          ...contact,
          mainPointOfContact: true,
          nameVisibility: formData.nameVisibility,
          email: formData.email,
          emailVisibility: formData.emailVisibility,
          phone: formData.phone,
          phoneVisibility: formData.phoneVisibility,
        };
      }

      return {
        ...contact,
        mainPointOfContact: false,
      };
    });
  const otherStudyContact = formData.studyContacts.find(
    (contact) => contact.firstName === "Other",
  );
  const isOtherSelected = !formData.studyContactId;

  const payload = {
    studyContacts: updatedStudyContacts,
    mainPointOfContact: isOtherSelected,
    recruitmentEmail: isOtherSelected
      ? formData.email
      : otherStudyContact.email,
    emailVisibility: isOtherSelected
      ? formData.emailVisibility
      : otherStudyContact.emailVisibility,
    recruitmentPhone: isOtherSelected
      ? formData.phone
      : otherStudyContact.phone,
    phoneVisibility: isOtherSelected
      ? formData.phoneVisibility
      : otherStudyContact.phoneVisibility,
    studyWebsiteContentRevisions: [
      {
        id: formData.versionId,
        contentStatus: "PUBLISHED",
        studyWebsiteContentDto: {
          title: formData.officialStudyTitle,
          shortTitle: formData.studyTitle,
          briefSummary: formData.purpose,
          inclusionCriteria: formData.inclusionCriteria,
          exclusionCriteria: formData.exclusionCriteria,
        },
      },
    ],
  };

  if (formData.prescreeningQuestionnaireUrl !== "") {
    payload.surveyUrl = formData.prescreeningQuestionnaireUrl;
  }

  if (formData.prescreeningQuestionnaireEstimatedTime !== "") {
    payload.surveyCompletionTimeEstimate = Number(
      formData.prescreeningQuestionnaireEstimatedTime,
    );
  }

  return payload;
};

/**
 * Helper method to transform the role field of study contacts to a more readable format.
 *
 * @param {object} data Study Recruitment data as recieved from the API call
 * @returns {Array} Array of study contacts with formatted roles
 */
const transformStudyContactsRole = (data) => {
  if (!data) return [];

  const transformedContacts = (data.studyContacts || []).map((contact) => {
    if (!contact.role) return contact;

    const formattedRole = contact.role
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return {
      ...contact,
      role: formattedRole,
    };
  });

  transformedContacts.push({
    ...initOtherStudyContact,
    mainPointOfContact: data?.mainPointOfContact,
    email: data?.recruitmentEmail,
    emailVisibility: data?.emailVisibility,
    phone: data?.recruitmentPhone,
    phoneVisibility: data?.phoneVisibility,
  });

  return transformedContacts;
};

/**
 * Helper method to format the main contact name for display.
 *
 * @param {object} mainContact The main contact object
 * @returns {string} Formatted main contact name
 */
export const formatMainContactName = (mainContact) => {
  if (!mainContact) return "";

  const { firstName, lastName, role } = mainContact;

  if (firstName === initOtherStudyContact.firstName) {
    return firstName;
  }

  return `${firstName} ${lastName} (${role})`;
};

/**
 * Helper method to format the versions data for display.
 *
 * @param {Array} versions Array of versions
 * @returns {Array} Array of versions with formatted data
 */
export const formatVersions = (versions) => {
  if (!versions) return [];

  return versions.map((version) => ({
    id: version.id,
    status: version.contentStatus
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" "),
    changedOn: format(new Date(version.createdDate), "MM/dd/yyyy h:mm a"),
    changedBy: version.updatedBy,
    version: version.studyWebsiteContentDto,
  }));
};

/**
 * Helper method to return only certain data and to filter out the list with the current protocol number
 */
export const marshalStudies = (studies) => {
  if (!studies) return [];

  return studies.map((study) => {
    const principalInvestigator = study.studyContacts?.find(
      (contact) => contact.role === "PRINCIPAL_INVESTIGATOR",
    );

    const piName = principalInvestigator
      ? ` - ${principalInvestigator.firstName} ${principalInvestigator.lastName}`
      : "";

    const sponsor =
      study?.sponsors?.length > 0 ? ` - ${study.sponsors[0]}` : "";

    return {
      protocolNumber: study.protocolNumber,
      name: `${study.protocolNumber} - ${study.irbNumber}${piName}${sponsor} - ${study.nickname}`,
    };
  });
};

export const marshalStudyRecruitmentMetrics = (metrics) => {
  if (!metrics) return {};

  return {
    oncoreStatus: metrics.oncoreStatus,
    totalAccrual: metrics.totalAccrual,
    targetAccrual: metrics.targetAccrual,
    percentAccrued: Math.ceil(
      (metrics.totalAccrual / metrics.targetAccrual) * 100,
    ),
    studyStatus: metrics.clinicalTrialsDirectoryStatus,
    studyListingStatus: metrics.studyListingStatus,
    lastOncoreSync: convertOncoreLastSyncTimeToDisplayFormat(
      metrics.oncoreLastSyncTime,
    ),
    outreachEnabled: metrics.meetsSelfServiceRecruitmentCriteria,
    schoolOfMedicineUrl: metrics?.schoolOfMedicineUrl || "",
    stanfordHealthCareUrl: metrics?.stanfordHealthCareUrl || "",
    pageViews: metrics.pageViews ? metrics.pageViews : 0,
    enrolledCountFromCt: metrics.enrolledCountFromCt,
    isHonestBrokerWorkflowEnabled: !metrics.isStudyTeamOutreachEnabled,
    isStudyTeamOutreachEnabled: metrics.isStudyTeamOutreachEnabled,
    totalOutreachCount: metrics.totalOutreachCount
      ? metrics.totalOutreachCount
      : 0,
    totalInterestedCount: metrics.totalInterestedCount
      ? metrics.totalInterestedCount
      : 0,
  };
};

export const marhsalCohortToApi = (
  studyTeamOutreachStatus,
  studyTeamOutreachResumeDate,
) => {
  const payload = { studyTeamOutreachStatus };

  if (studyTeamOutreachResumeDate) {
    payload.studyTeamOutreachResumeDate = studyTeamOutreachResumeDate;
  }

  return payload;
};

/**
 * Takes a UsersService user object and determines which actions
 * they are permitted to take & generates flags to be used in the
 * StudyRecruitmentService & components.
 *
 * @param {object} user A UsersService user object
 * @returns Permissions flags object
 */
export const generatePermissions = (user) => {
  const flags = { ...initUserPerms };
  const roles = user?.roles || [];

  flags.canViewStudyRecruitment = !!roles.some(
    (userRole) =>
      userRole.role === "HONEST_BROKER" || userRole.role === "SYSTEM_ADMIN",
  );

  return flags;
};
